import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import searchIcon from '@/shared/assets/images/search_icon.svg';
import filterIcon from '@/shared/assets/images/filter_icon.svg';
import refreshIcon from '@/shared/assets/images/refresh_icon.svg';
import chevronDownIcon from '@/shared/assets/images/chevron_down_icon.svg';

import styles from './MobileSearchBar.module.scss';

/**
 * 모바일 검색/정렬 바 컴포넌트
 * - 검색 입력
 * - 정렬 드롭다운
 * - 필터 버튼
 * - 초기화 버튼
 */
function MobileSearchBar({
  searchValue,
  onSearchChange,
  onSearch,
  sortOptions,
  sortValue,
  onSortChange,
  onFilterClick,
  onResetClick,
  filterCount,
  placeholder,
}) {
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortRef = useRef(null);

  // 정렬 드롭다운 외부 클릭 시 닫기
  useEffect(function () {
    function handleClickOutside(event) {
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setIsSortOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return function () {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  function handleSortSelect(option) {
    onSortChange(option.value);
    setIsSortOpen(false);
  }

  function handleKeyDown(event) {
    if (event.key === 'Enter') {
      onSearch();
    }
  }

  // 현재 선택된 정렬 옵션의 라벨
  const selectedSortLabel =
    sortOptions.find(function (opt) {
      return opt.value === sortValue;
    })?.label || '정렬';

  return (
    <div className={styles.container}>
      {/* 검색 입력 */}
      <div className={styles.searchWrapper}>
        <img src={searchIcon} alt="검색" className={styles.searchIcon} />
        <input
          type="text"
          className={styles.searchInput}
          placeholder={placeholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </div>

      {/* 정렬/필터 버튼 영역 */}
      <div className={styles.actionWrapper}>
        {/* 정렬 드롭다운 */}
        <div className={styles.sortContainer} ref={sortRef}>
          <button
            type="button"
            className={styles.sortButton}
            onClick={() => setIsSortOpen(!isSortOpen)}
          >
            <span className={styles.sortLabel}>{selectedSortLabel}</span>
            <img
              src={chevronDownIcon}
              alt=""
              className={`${styles.sortChevron} ${isSortOpen ? styles.sortChevronOpen : ''}`}
            />
          </button>

          {isSortOpen && (
            <ul className={styles.sortDropdown}>
              {sortOptions.map(function (option) {
                return (
                  <li key={option.value}>
                    <button
                      type="button"
                      className={`${styles.sortOption} ${sortValue === option.value ? styles.sortOptionActive : ''}`}
                      onClick={() => handleSortSelect(option)}
                    >
                      {option.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* 필터 버튼 */}
        <button type="button" className={styles.filterButton} onClick={onFilterClick}>
          <img src={filterIcon} alt="필터" className={styles.filterIcon} />
          {filterCount > 0 && <span className={styles.filterBadge}>{filterCount}</span>}
        </button>

        {/* 초기화 버튼 */}
        <button
          type="button"
          className={styles.resetButton}
          onClick={onResetClick}
          aria-label="필터 초기화"
        >
          <img src={refreshIcon} alt="초기화" className={styles.resetIcon} />
        </button>
      </div>
    </div>
  );
}

MobileSearchBar.propTypes = {
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  onSearch: PropTypes.func,
  sortOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  sortValue: PropTypes.string,
  onSortChange: PropTypes.func,
  onFilterClick: PropTypes.func,
  onResetClick: PropTypes.func,
  filterCount: PropTypes.number,
  placeholder: PropTypes.string,
};

MobileSearchBar.defaultProps = {
  searchValue: '',
  onSearchChange: () => {},
  onSearch: () => {},
  sortOptions: [
    { value: 'latest', label: '최신순' },
    { value: 'oldest', label: '오래된순' },
  ],
  sortValue: 'latest',
  onSortChange: () => {},
  onFilterClick: () => {},
  onResetClick: () => {},
  filterCount: 0,
  placeholder: '검색어를 입력하세요',
};

export default MobileSearchBar;
